import type { Visualizer } from './types';
import { clearBg, drawHud } from './types';

/**
 * Flowing neon waveform ribbons in pseudo-perspective.
 */
export const ribbonViz: Visualizer = {
  id: 'ribbon',
  label: 'Wave Ribbons',
  draw(frame) {
    const { ctx, w, h, wave, bands, t, freq } = frame;
    clearBg(ctx, w, h, 0.35);

    const ribbons = 7;
    for (let r = 0; r < ribbons; r++) {
      const depth = r / (ribbons - 1);
      const y0 = h * (0.22 + depth * 0.55);
      const amp = h * (0.03 + (1 - depth) * 0.07) * (0.7 + bands.energy * 0.7);
      const lag = Math.floor(depth * 40 + bands.mids * 20);
      const thick = 2 + (1 - depth) * 5 + bands.kick * 2;

      // filled ribbon (wave + offset edge)
      ctx.beginPath();
      const pts: { x: number; y: number }[] = [];
      const step = Math.max(2, Math.floor(wave.length / Math.min(280, w / 3)));
      for (let i = 0; i < wave.length; i += step) {
        const u = i / (wave.length - 1);
        const x = u * w;
        const s = wave[(i + lag) % wave.length] || 0;
        const wobble = Math.sin(u * 10 + t * (1.2 + depth) + r) * amp * 0.25;
        const y = y0 + s * amp * 16 + wobble;
        pts.push({ x, y });
      }
      for (let i = 0; i < pts.length; i++) {
        if (i === 0) ctx.moveTo(pts[i].x, pts[i].y);
        else ctx.lineTo(pts[i].x, pts[i].y);
      }
      // return path offset by thickness downward
      for (let i = pts.length - 1; i >= 0; i--) {
        const bin = Math.floor((i / pts.length) * (freq.length - 1));
        const lift = thick + (freq[bin] / 255) * thick * 1.8;
        ctx.lineTo(pts[i].x, pts[i].y + lift);
      }
      ctx.closePath();

      const hue = 155 + depth * 50 + bands.highs * 30 + r * 6;
      const g = ctx.createLinearGradient(0, y0 - amp * 2, 0, y0 + amp * 3);
      g.addColorStop(0, `hsla(${hue}, 95%, 65%, ${0.15 + (1 - depth) * 0.35})`);
      g.addColorStop(0.5, `hsla(${hue + 15}, 90%, 50%, ${0.2 + bands.energy * 0.25})`);
      g.addColorStop(1, `hsla(${hue + 30}, 80%, 35%, 0.05)`);
      ctx.fillStyle = g;
      ctx.fill();
      ctx.strokeStyle = `hsla(${hue}, 100%, 70%, ${0.35 + (1 - depth) * 0.4})`;
      ctx.lineWidth = 1 + (1 - depth);
      ctx.shadowColor = `hsla(${hue}, 100%, 60%, 0.45)`;
      ctx.shadowBlur = 8 + bands.beat * 10;
      ctx.stroke();
      ctx.shadowBlur = 0;
    }

    // vertical frequency ticks
    const n = 32;
    for (let i = 0; i < n; i++) {
      const x = ((i + 0.5) / n) * w;
      const v = freq[Math.floor((i / n) * freq.length)] / 255;
      ctx.fillStyle = `rgba(86,240,200,${0.04 + v * 0.12})`;
      ctx.fillRect(x, 0, 1, h * v * 0.25);
    }

    drawHud(frame);
  },
};
